/**
 * Types minimaux pour la Web Speech API (`SpeechRecognition`) — absente de
 * lib.dom.d.ts (spec non finalisée côté W3C/TS). On ne déclare QUE ce qu'on
 * utilise réellement, pas l'API complète.
 */

export interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

export interface SpeechRecognitionResultLike {
  readonly length: number;
  readonly isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}

export interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}

export interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}

export interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
}

export interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  /**
   * `audioTrack` (spec récente, Chrome) : écouter CETTE piste plutôt que le
   * micro par défaut du système. Ignoré (argument en trop) par les navigateurs
   * qui ne le supportent pas.
   */
  start(audioTrack?: MediaStreamTrack): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: ((event: Event) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onstart: ((event: Event) => void) | null;
  onaudiostart: ((event: Event) => void) | null;
  onspeechstart: ((event: Event) => void) | null;
  onnomatch: ((event: Event) => void) | null;
}

export interface SpeechRecognitionConstructorLike {
  new (): SpeechRecognitionLike;
}

/** `SpeechRecognition` (standard) ou `webkitSpeechRecognition` (Chrome/Safari), selon le navigateur. */
export function getSpeechRecognitionCtor(): SpeechRecognitionConstructorLike | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructorLike;
    webkitSpeechRecognition?: SpeechRecognitionConstructorLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
